import {Descriptor, MipLevelRegion, Region, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {ExtRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/definition"
import {Box2, isBox2Like, Size2Like, wrap} from "@cm/math"
import {assertNever} from "@cm/utils"
import {RegionDependencyGraph} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-dependency/region-dependency-graph"
import {AddressMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {getMipLevelSize} from "@cm/hal/hal-image/utils"
import {AnyImageOpHalData, MapToDataType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"

export const determineDescriptor = (sourceImageDescriptor: Descriptor, regionSize: Size2Like): Descriptor => {
    return {
        ...sourceImageDescriptor,
        width: regionSize.width,
        height: regionSize.height,
    }
}

export const getSourceImageDependencyGraph = (args: {sourceSize: Size2Like; sourceRegion: Region; sourceAddressMode: AddressMode}) => {
    const dependency = new RegionDependencyGraph({
        sourceSize: args.sourceSize,
        sourceAddressMode: args.sourceAddressMode,
        targetSize: {
            width: args.sourceRegion.width,
            height: args.sourceRegion.height,
        },
        targetAddressMode: "clamp",
    })
    dependency.createStream().offset({x: -args.sourceRegion.x, y: -args.sourceRegion.y})
    return dependency
}

export const determineSourceImageMipLevelRegion = (
    sourceImageDescriptor: Descriptor,
    region: MapToDataType<ExtRegion>,
    mipLevelOffset: number,
    targetMipLevelRegion: MipLevelRegion,
): MipLevelRegion => {
    return {
        ...getSourceRegion(sourceImageDescriptor, region, targetMipLevelRegion),
        mipLevel: targetMipLevelRegion.mipLevel + mipLevelOffset,
    }
}

export const getSourceRegion = (sourceImageDescriptor: Descriptor, region: MapToDataType<ExtRegion>, mipLevelRegion: MipLevelRegion): Region => {
    if (!isBox2Like(region)) {
        // if we have a dynamic source region (offsetImage), we update the entire source image
        return {
            x: 0,
            y: 0,
            ...getMipLevelSize(sourceImageDescriptor, mipLevelRegion.mipLevel),
        }
    } else {
        const mipLevelScale = 1 / (1 << mipLevelRegion.mipLevel)
        const p00 = {
            x: Math.floor(mipLevelRegion.x + region.x * mipLevelScale),
            y: Math.floor(mipLevelRegion.y + region.y * mipLevelScale),
        }
        const p11 = {
            x: Math.ceil(mipLevelRegion.x + region.x * mipLevelScale + Math.min(mipLevelRegion.width, region.width * mipLevelScale)),
            y: Math.ceil(mipLevelRegion.y + region.y * mipLevelScale + Math.min(mipLevelRegion.height, region.height * mipLevelScale)),
        }
        return {
            x: p00.x,
            y: p00.y,
            width: p11.x - p00.x,
            height: p11.y - p00.y,
        }
    }
}

export const determineTargetImageRegion = (
    region: MapToDataType<ExtRegion>,
    addressMode: AddressMode,
    sourceImage: AnyImageOpHalData,
    sourceRegions: Regions,
): Regions => {
    if (!isBox2Like(region)) {
        // if we have a dynamic source region (offsetImage), we invalidate the entire image
        return [
            {
                x: 0,
                y: 0,
                width: region.width,
                height: region.height,
            },
        ]
    } else {
        return sourceRegions.flatMap((sourceRegion) => {
            switch (addressMode) {
                case "clamp":
                case "border": {
                    return Box2.intersect(
                        {
                            x: sourceRegion.x - region.x,
                            y: sourceRegion.y - region.y,
                            width: sourceRegion.width,
                            height: sourceRegion.height,
                        },
                        {
                            x: 0,
                            y: 0,
                            width: region.width,
                            height: region.height,
                        },
                    )
                }
                case "wrap": {
                    const baseOffset = {
                        x: wrap(region.x, sourceImage.descriptor.width),
                        y: wrap(region.y, sourceImage.descriptor.height),
                    }
                    const wrapOffsets = [
                        {x: 0, y: 0},
                        {x: sourceImage.descriptor.width, y: 0},
                        {x: 0, y: sourceImage.descriptor.height},
                        {x: sourceImage.descriptor.width, y: sourceImage.descriptor.height},
                    ]
                    return wrapOffsets
                        .flatMap((offset) =>
                            Box2.intersect(
                                {
                                    x: sourceRegion.x - baseOffset.x + offset.x,
                                    y: sourceRegion.y - baseOffset.y + offset.y,
                                    width: sourceRegion.width,
                                    height: sourceRegion.height,
                                },
                                {
                                    x: 0,
                                    y: 0,
                                    width: region.width,
                                    height: region.height,
                                },
                            ),
                        )
                        .filter((region) => !Box2.isEmpty(region))
                }
                case "wrap-mirrored":
                    throw Error("Wrap-mirrored address mode is not implemented in extract-region's onSourceRegionsInvalidated")
                default:
                    assertNever(addressMode)
            }
        })
    }
}
